import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import { toggleSpinner } from 'apps/auth/actions/appAction';
import { addExtras } from 'apps/auth/actions/extraAction';
import { setNewForm } from 'apps/auth/actions/formAction';
import { updatePageMessages } from 'apps/auth/actions/messageAction';
import BaseLayout from 'apps/auth/components/common/Layout/BaseLayout';
import { InfoPage } from 'apps/auth/components/common/Layout/InfoPage/InfoPage';
import { getField } from 'apps/auth/components/form-multi/utils';
import Failed from 'apps/auth/components/sharedComponents/status/Failed';
import Pending from 'apps/auth/components/sharedComponents/status/Pending';
import SeamlessRetry from 'apps/auth/components/sharedComponents/status/SeamlessRetry';
import Success from 'apps/auth/components/sharedComponents/status/Success';
import Waiting from 'apps/auth/components/sharedComponents/status/Waiting';
import BasePage from 'apps/auth/components/utils/BasePage';
import ModalNew from 'apps/auth/components/utils/ModalNew';
import { STATUS_OK, STATUS_PENDING } from 'apps/auth/constants/Constants';
import { SMS_ON_SUCCESS_PAY_FORM_NAME } from 'apps/auth/constants/formName';
import { GA4_SEND_FIRST_SUCCESS } from 'apps/auth/constants/GoogleAnalyticsEvents';
import { AUTH_PAY_NOTIFICATION_ACTION, AUTH_SEND_MAIN_LINK } from 'apps/auth/constants/UrlConstants';
import { apiFetchData } from 'apps/auth/helpers/api';
import { logWarn } from 'apps/auth/helpers/clientLogger';
import { useExtra, usePageMessages } from 'apps/auth/hooks/selectors';

function useFirstTransferAnalytics(status, isFirstTransfer) {
    useEffect(() => {
        if (__isBrowser__) {
            window.addEventListener(
                'load',
                function () {
                    if (status && status.status <= STATUS_OK && isFirstTransfer) {
                        window.dataLayer = window.dataLayer || [];
                        window.dataLayer.push({ event: GA4_SEND_FIRST_SUCCESS });
                    }
                },
                { once: true },
            );
        }
    }, []);
}

const StatusAuthPage = (props) => {
    const messages = usePageMessages();
    const { special_error_code, show_additional_check_button, isFirstTransfer, statusBean, setRefreshTimer, cheque } = useExtra();
    const modalRef = useRef();
    const [loading, setLoading] = useState(true);

    const [status, setStatus] = useState(props.data?.status ? props.data?.status : props.data?.extra?.payment_status);

    useEffect(() => {
        if (props.data.smsOnSuccessPay) {
            props.setNewForm(
                SMS_ON_SUCCESS_PAY_FORM_NAME,
                {
                    id: getField('id', props.data.smsOnSuccessPay.id, null, true, false),
                    phone: getField('phone', '', null, true, false, messages.sendPhonePlaceholder, 'tel'),
                    text: getField('text', messages.sendTextText, null, true, false, messages.sendTextPlaceholder, 'text'),
                },
                AUTH_PAY_NOTIFICATION_ACTION,
            );
        }
    }, []);

    useEffect(() => {
        let attempt = 1;
        let intervalId;
        if (loading) {
            props.toggleSpinner(true);
            intervalId = setInterval(() => {
                apiFetchData(`/auth/pay/status/info/${statusBean?.id}`)
                    .then((json) => {
                        setStatus(json.status);
                        props.updatePageMessages(json.messages);
                        props.addExtras(json.extra);

                        if (attempt++ >= json.extra?.numberOfAttempts || json.status.status >= STATUS_OK) {
                            setLoading(false);
                            props.toggleSpinner(false);
                            clearInterval(intervalId);
                        }
                    })
                    .catch((e) => {
                        logWarn(e);
                        setLoading(false);
                        props.toggleSpinner(false);
                        clearInterval(intervalId);
                    });
            }, setRefreshTimer);
        }

        return () => clearInterval(intervalId);
    }, [loading]);

    useFirstTransferAnalytics(status, isFirstTransfer);

    const getStatus = () => {
        if (loading) {
            return <div />;
        } else if (status && status.status <= STATUS_PENDING) {
            return <Pending toggleSpinner={props.toggleSpinner} />;
        } else if (status && status.status === STATUS_OK) {
            return (
                <Success
                    smsOnSuccessPay={props.data.smsOnSuccessPay}
                    showSendSMSNotificationBlock={status.showSendSMSNotificationBlock}
                    status={status}
                    modalRef={modalRef}
                />
            );
        } else if (status && status.fromBankCardFunding && status.fundingDeclined && cheque != null && cheque?.billId === 0) {
            return <SeamlessRetry status={status} toggleSpinner={props.toggleSpinner} modalRef={modalRef} dataTestId="seamless-retry-status-container" />;
        } else if (status && status.fromBankCardFunding && status.payoutFailed && cheque != null && cheque?.billId === 0) {
            return <Failed status={status} modalRef={modalRef} toggleSpinner={props.toggleSpinner} />;
        } else if (status && status.status > STATUS_OK) {
            return <Failed status={status} modalRef={modalRef} toggleSpinner={props.toggleSpinner} />;
        } else if (!loading) {
            return <Waiting smsOnSuccessPay={props.data.smsOnSuccessPay} status={status} />;
        }
    };

    if (special_error_code) {
        return (
            <InfoPage
                animatedIconFail
                headerText={messages.error_title}
                secondaryText={messages.error_description}
                primaryButtonType="reactLink"
                primaryButtonCaption={show_additional_check_button ? messages.additional_check_button : ''}
                primaryButtonLink={`/auth/user-error?code=${special_error_code}`}
                topButtonCaption={messages.cancelText}
                topButtonType="link"
                topButtonLink={AUTH_SEND_MAIN_LINK}
            />
        );
    } else {
        return (
            <BaseLayout>
                {getStatus()}
                <ModalNew ref={modalRef} />
            </BaseLayout>
        );
    }
};

export default connect(null, { setNewForm, toggleSpinner, updatePageMessages, addExtras })(BasePage(StatusAuthPage));
